import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@nexus/ui";
import {
  NumberField,
  SettingRow,
  SettingSwitch,
  SettingsCard,
} from "@/features/changelog/components/shared/SettingsCard";
import {
  BYTES_PER_GIB,
  LIMITS,
  MICROS_PER_CENT,
  SPEND_ALERT_WINDOWS,
} from "./ops-settings-limits";
import { SlackWarning } from "./SlackWarning";
import type { OpsSettingsCardProps } from "./types";

/**
 * What BigQuery usage costs, how long it is kept, and when it is worth a Slack
 * message.
 *
 * The rate and the retention dial both do real work: the rate restates every
 * cost figure the tab has ever shown, because executions record bytes and never
 * a price, and retention deletes cross-tenant history for good. Both are
 * audited for that reason.
 */
export function BigQuerySettingsCard({
  draft,
  set,
  isReadOnly,
  isSlackConfigured,
}: OpsSettingsCardProps) {
  const { t } = useTranslation();
  const anyAlertOn = draft.alertOnBigQueryBytes || draft.alertOnBigQuerySingleQueryBytes;

  return (
    <SettingsCard
      icon="columns"
      title={t("admin.opsSettings.bigQuery.title", "BigQuery usage")}
      description={t(
        "admin.opsSettings.bigQuery.description",
        "What every BigQuery query scanned. The rate every cost on the tab is computed at, how long the record is kept, and when a runaway bill is worth a Slack message.",
      )}
    >
      <SettingRow
        htmlFor="bigquery-price-per-tb"
        label={t("admin.opsSettings.bigQueryPrice", "Price per TB scanned")}
        description={t(
          "admin.opsSettings.bigQueryPriceHelp",
          "BigQuery's on-demand rate is 625 cents per TB. Leave it empty and the tab shows no costs at all rather than zeroes — and changing it restates every figure, including past windows, because queries record bytes and never a price.",
        )}
        control={
          <NumberField
            id="bigquery-price-per-tb"
            value={Math.round(draft.bigQueryPricePerTbMicros / MICROS_PER_CENT)}
            min={LIMITS.bigQueryPricePerTbCents.min}
            max={LIMITS.bigQueryPricePerTbCents.max}
            unit={t("admin.opsSettings.centsPerTb", "cents / TB")}
            disabled={isReadOnly}
            onChange={(value) => set("bigQueryPricePerTbMicros", value * MICROS_PER_CENT)}
          />
        }
      />

      <SettingRow
        htmlFor="bigquery-retention-days"
        label={t("admin.opsSettings.bigQueryRetention", "Keep query history for")}
        description={t(
          "admin.opsSettings.bigQueryRetentionHelp",
          "This one has no keep-forever setting: a row is written for every query the platform runs, so the history is bounded whether anyone chooses a number or not. Old rows are deleted for good, about once an hour.",
        )}
        control={
          <NumberField
            id="bigquery-retention-days"
            value={draft.bigQueryRetentionDays}
            min={LIMITS.bigQueryRetentionDays.min}
            max={LIMITS.bigQueryRetentionDays.max}
            unit={t("admin.opsSettings.days", "days")}
            disabled={isReadOnly}
            onChange={(value) => set("bigQueryRetentionDays", value)}
          />
        }
      />

      <SettingRow
        label={t("admin.opsSettings.bigQueryAlertWindow", "Measure over")}
        description={t(
          "admin.opsSettings.bigQueryAlertWindowHelp",
          "The stretch both alerts below read. One window for both, so the two messages can never describe different spans of the same incident.",
        )}
        control={
          <Select
            value={String(draft.bigQueryBytesAlertWindowHours)}
            disabled={isReadOnly}
            onValueChange={(value) => set("bigQueryBytesAlertWindowHours", Number(value))}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SPEND_ALERT_WINDOWS.map((hours) => (
                <SelectItem key={hours} value={String(hours)} className="text-xs">
                  {t("admin.opsSettings.lastHours", {
                    defaultValue_one: "The last hour",
                    defaultValue_other: "The last {{count}} hours",
                    count: hours,
                  })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <SettingRow
        htmlFor="bigquery-alert-cooldown"
        label={t("admin.opsSettings.alertCooldown", "Stay quiet for")}
        description={t(
          "admin.opsSettings.bigQueryAlertCooldownHelp",
          "After an alert fires, how long before it may fire again. The two alerts below serve this quiet period separately, so neither can mute the other.",
        )}
        control={
          <NumberField
            id="bigquery-alert-cooldown"
            value={draft.bigQueryBytesAlertCooldownHours}
            min={LIMITS.bigQueryBytesAlertCooldownHours.min}
            max={LIMITS.bigQueryBytesAlertCooldownHours.max}
            unit={t("admin.opsSettings.hours", "hours")}
            disabled={isReadOnly}
            onChange={(value) => set("bigQueryBytesAlertCooldownHours", value)}
          />
        }
      />

      <SettingRow
        htmlFor="bigquery-bytes-alert"
        label={t("admin.opsSettings.bigQueryBytesAlert", "Post to Slack when the window runs away")}
        description={t(
          "admin.opsSettings.bigQueryBytesAlertHelp",
          "Totalled across every feature and tenant. The message breaks the window down by both, so the first question the channel asks is already answered.",
        )}
        control={
          <SettingSwitch
            id="bigquery-bytes-alert"
            checked={draft.alertOnBigQueryBytes}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnBigQueryBytes", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="bigquery-bytes-threshold"
        isNested
        isMuted={!draft.alertOnBigQueryBytes}
        label={t("admin.opsSettings.bigQueryBytesThreshold", "Alert when the window passes")}
        description={t(
          "admin.opsSettings.bigQueryBytesThresholdHelp",
          "In bytes rather than dollars, so the alert still works when no rate is set — which is the ordinary state.",
        )}
        control={
          <NumberField
            id="bigquery-bytes-threshold"
            value={Math.round(draft.bigQueryBytesAlertThreshold / BYTES_PER_GIB)}
            min={LIMITS.bigQueryBytesAlertThresholdGib.min}
            max={LIMITS.bigQueryBytesAlertThresholdGib.max}
            unit={t("admin.opsSettings.gibibytes", "GiB")}
            disabled={isReadOnly || !draft.alertOnBigQueryBytes}
            onChange={(value) => set("bigQueryBytesAlertThreshold", value * BYTES_PER_GIB)}
          />
        }
      />

      <SettingRow
        htmlFor="bigquery-single-query-alert"
        label={t(
          "admin.opsSettings.bigQuerySingleQueryAlert",
          "Post to Slack when one query runs away",
        )}
        description={t(
          "admin.opsSettings.bigQuerySingleQueryAlertHelp",
          "The window total hides one runaway statement until it is repeated. The message names the feature, tenant and job id so the query can be found.",
        )}
        control={
          <SettingSwitch
            id="bigquery-single-query-alert"
            checked={draft.alertOnBigQuerySingleQueryBytes}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnBigQuerySingleQueryBytes", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="bigquery-single-query-threshold"
        isNested
        isMuted={!draft.alertOnBigQuerySingleQueryBytes}
        label={t(
          "admin.opsSettings.bigQuerySingleQueryThreshold",
          "Alert when one query passes",
        )}
        description={t(
          "admin.opsSettings.bigQuerySingleQueryThresholdHelp",
          "Set below the window threshold — one statement reaching the window figure on its own is already an incident.",
        )}
        control={
          <NumberField
            id="bigquery-single-query-threshold"
            value={Math.round(draft.bigQuerySingleQueryBytesThreshold / BYTES_PER_GIB)}
            min={LIMITS.bigQueryBytesAlertThresholdGib.min}
            max={LIMITS.bigQueryBytesAlertThresholdGib.max}
            unit={t("admin.opsSettings.gibibytes", "GiB")}
            disabled={isReadOnly || !draft.alertOnBigQuerySingleQueryBytes}
            onChange={(value) => set("bigQuerySingleQueryBytesThreshold", value * BYTES_PER_GIB)}
          />
        }
      />

      {anyAlertOn && !isSlackConfigured ? <SlackWarning /> : null}
    </SettingsCard>
  );
}
